import React, { useEffect, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import {
  bool, func, number, node, string
} from 'prop-types';
import classnames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';

import { noop } from '../../utils/promo-utils';
import { PromoSidecarNav } from './subcomponents/promo-sidecar-nav/PromoSidecarNav';
import { PromoSidecarCta } from './subcomponents/promo-sidecar-cta/PromoSidecarCta';
import { PromoSidecarCart } from './subcomponents/promo-sidecar-cart/PromoSidecarCart';
import { usePromoPresentation } from '../../contexts/PromoPresentationProvider';
import './PromoSidecar.scss';

export const PromoSidecar = ({
  isSidecarOpen,
  setIsSidecarOpen,
  zIndex,
  useSidecarMask,
  useSidecarNav,
  useSidecarCta,
  useSidecarCart,
  backText,
  itemId,
  children,
  selected,
  onSelect,
  setQuantityWarningText,
  showQuantityWarningState,
  displayPosition
}) => {
  const { channel } = useContext(ExperienceContext);
  const { isDrawerOpen, setIsDrawerOpen, isAppleTouchDevice } = usePromoPresentation();

  const closeSidecar = useCallback((event) => {
    if (event) event.preventDefault();
    setIsSidecarOpen(false);
  }, [setIsSidecarOpen]);

  const closeAll = useCallback((event) => {
    if (event) event.preventDefault();
    setIsDrawerOpen(false);
    setIsSidecarOpen(false);
  }, [setIsSidecarOpen, setIsDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) {
      setIsSidecarOpen(false);
    }
  }, [isDrawerOpen, setIsSidecarOpen]);

  useEffect(() => {
    if (useSidecarMask) {
      if (isSidecarOpen) {
        // IE doesn't support adding/removing multiple classes at the same time.
        document.body.classList.add('promo-stop-body-scroll');
        if (channel === 'desktop' && !isAppleTouchDevice) {
          document.body.classList.add('promo-stop-body-scroll--desktop');
        }
      } else {
        document.body.classList.remove('promo-stop-body-scroll');
        if (channel === 'desktop' && !isAppleTouchDevice) {
          document.body.classList.remove('promo-stop-body-scroll--desktop');
        }
      }
    }
  }, [isSidecarOpen, useSidecarMask, channel, isAppleTouchDevice]);

  const maskClasses = classnames('promo-sidecar-mask', {
    'promo-sidecar-mask--open': isSidecarOpen,
  });

  const sidecarClasses = classnames('promo-sidecar', {
    'promo-sidecar--open': isSidecarOpen,
  });

  const sidecarBodyClasses = classnames('promo-sidecar-body', {
    'promo-sidecar-body--with-cta': useSidecarCta || useSidecarCart,
    'promo-sidecar-body--with-nav': useSidecarNav,
  });

  return (
    <>
      {typeof window !== 'undefined' && createPortal(
        <>
          {useSidecarMask && (
            <div
              className={maskClasses}
              data-testid={maskClasses}
              role="button"
              aria-label="close the promotion drawer"
              tabIndex={0}
              onClick={closeAll}
            />
          )}
          <aside style={{ zIndex }} className={sidecarClasses}>
            {useSidecarNav && (
              <PromoSidecarNav backText={backText} closeSidecar={closeSidecar} closeDrawer={closeAll} />
            )}

            <section className={sidecarBodyClasses}>{children}</section>

            {useSidecarCta && itemId && (
              <PromoSidecarCta
                itemId={itemId}
                onSelect={onSelect}
                selected={selected}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
                displayPosition={displayPosition}
              />
            )}
            {!useSidecarCta && useSidecarCart && <PromoSidecarCart />}
          </aside>
        </>, document.body
      )}
    </>
  );
};

PromoSidecar.displayName = 'PromoSidecar';

PromoSidecar.propTypes = {
  isSidecarOpen: bool.isRequired,
  setIsSidecarOpen: func.isRequired,
  children: node.isRequired,
  zIndex: number,
  useSidecarMask: bool,
  useSidecarCta: bool,
  useSidecarCart: bool,
  useSidecarNav: bool,
  backText: string,
  itemId: string,
  selected: bool,
  onSelect: func,
  setQuantityWarningText: func,
  showQuantityWarningState: bool,
  displayPosition: string
};

PromoSidecar.defaultProps = {
  zIndex: 1000,
  useSidecarMask: false,
  useSidecarCta: false,
  useSidecarNav: false,
  useSidecarCart: false,
  backText: 'Back',
  itemId: '',
  selected: false,
  onSelect: noop,
  setQuantityWarningText: noop,
  showQuantityWarningState: false,
  displayPosition: '',
};
